import { COLUMN_KEYS, columnDefs, type ColumnKey } from './columns'
import { belowFloor, type Row } from './partition'
import { adjustedPartial } from './metrics'
import { audienceCell, formatLabel, labelOf, qualifiedLabel, verdictLabel, verdictQualifier, type ViewContext } from './view'
import type { Period } from '../lib/period'

/**
 * The creatives table as a file (GRO-64 design, D6 and D7; GRO-81 design, D1 and D3).
 *
 * One pure function from the rows the table shows to typed cells, and one from those cells
 * to the text of an RFC 4180 file, so every rule below is testable without a browser. The
 * `.xlsx` writer in `xlsx.ts` reads the same cells. The rows arrive in the table's order -
 * the screen splits them with `park` first - and are written in the order given.
 *
 * The file is for a spreadsheet, so a figure is a plain number: no grouping, no currency
 * symbol, the currency named in the header instead. What the screen marks, the file marks
 * in the same characters: `*` after a figure part of which was not observed, `~` before a
 * figure the sample cannot rank. A marked figure is a text cell, which is the point: it
 * must not be summed as if it were a plain number.
 */

const MONEY: ReadonlySet<ColumnKey> = new Set<ColumnKey>(['spend', 'cpa', 'aov', 'cpc', 'cpm'])

/** The byte-order mark, without which Excel reads UTF-8 Cyrillic as mojibake. */
const BOM = '﻿'
const EOL = '\r\n'

/**
 * One cell of the export, typed. A plain figure is a number the spreadsheet can compute
 * with; a marked figure, a label or a name is text; a missing value is nothing at all.
 */
export type ExportCell =
  | { kind: 'number'; value: number; decimals: number }
  | { kind: 'text'; text: string }
  | { kind: 'empty' }

export interface ExportTable {
  header: string[]
  records: ExportCell[][]
}

const textCell = (text: string): ExportCell => ({ kind: 'text', text })

interface Figure {
  value: number | null
  decimals: number
  partial?: boolean
  unrankable?: boolean
}

/**
 * GRO-60's four states, kept apart: `0`, `0*`, an empty cell, a lone `*`.
 *
 * An unmarked null is no value and nothing missing, which covers a measure that does not
 * apply; a marked null is no value because part of the figure was not observed. The dash
 * glyph the screen draws for both never reaches the file.
 */
function figure({ value, decimals, partial = false, unrankable = false }: Figure): ExportCell {
  if (value === null || !Number.isFinite(value)) return partial ? textCell('*') : { kind: 'empty' }
  // `(-0.001).toFixed(2)` is "-0.00", a negative zero no reader would recognise.
  const rounded = Number(value.toFixed(decimals)) || 0
  if (!partial && !unrankable) return { kind: 'number', value: rounded, decimals }
  return textCell(`${unrankable ? '~' : ''}${rounded.toFixed(decimals)}${partial ? '*' : ''}`)
}

/**
 * A CSV text field a spreadsheet will not evaluate.
 *
 * Creative names come from ad names a customer's team types, and a leading `=`, `+`, `-`
 * or `@` - or a tab or carriage return in front of one - makes Excel and Sheets read the
 * cell as a formula. The apostrophe is the conventional neutraliser, and it shows. The
 * `.xlsx` needs none: an inline string is never evaluated.
 */
function text(value: string): string {
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value
}

/** RFC 4180: quote a field holding a comma, a quote or a line break; double its quotes. */
function field(value: string): string {
  return /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value
}


function accountsCell(row: Row, ctx: ViewContext): string {
  const ids = [...new Set(row.ads.map((ad) => ad.accountId))]
  return ids
    .map((id) => {
      const account = ctx.accounts.find((candidate) => candidate.id === id)
      const labels = [
        ...(account?.isDemo ? [ctx.t.demoMark] : []),
        ...(account?.lagging ? [ctx.t.csvDataDelayed] : []),
      ]
      const short = account?.short ?? id
      return labels.length > 0 ? `${short} (${labels.join(', ')})` : short
    })
    .join('; ')
}

function cell(row: Row, key: ColumnKey, ctx: ViewContext): ExportCell {
  const { t, lang } = ctx
  const incomplete = (measure: Row['metrics']['incomplete'][number]) => row.metrics.incomplete.includes(measure)
  switch (key) {
    case 'name':
      return textCell(row.displayName)
    case 'accounts':
      return textCell(accountsCell(row, ctx))
    case 'format':
      return textCell(formatLabel(row.format, t))
    case 'hypothesis': {
      // One «Идея и теги» cell (GRO-170): the idea, then the visual when one is set.
      const idea = labelOf(ctx.dimensions.hypotheses, row.hypothesisId, lang) ?? t.hypUnassigned
      const visual = labelOf(ctx.dimensions.techniques, row.techniqueId, lang)
      return textCell(visual ? `${idea} · ${visual}` : idea)
    }
    case 'audience':
      return textCell(audienceCell(row, ctx.dimensions.audiences, lang, t))
    case 'spend':
      return figure({ value: row.metrics.spend, decimals: 2, partial: incomplete('spend') })
    case 'share':
      // An unknown share is an empty cell, as the table shows the glyph unmarked.
      return figure({ value: row.share.pct, decimals: 0, partial: row.share.pct !== null && row.share.partial })
    case 'hook':
      return figure({ value: row.derived.hookRate.value, decimals: 1, partial: row.derived.hookRate.partial })
    case 'hold':
      return figure({ value: row.derived.holdRate.value, decimals: 1, partial: row.derived.holdRate.partial })
    case 'ctr':
      return figure({ value: row.derived.ctr.value, decimals: 2, partial: row.derived.ctr.partial })
    case 'purch':
      return figure({ value: row.metrics.conversions, decimals: 0, partial: incomplete('conversions') })
    case 'roas':
      return figure({ value: row.derived.roas.value, decimals: 2, partial: row.derived.roas.partial, unrankable: belowFloor(row, ctx.thresholds) })
    case 'adj':
      return figure({ value: row.adjustedRoas, decimals: 2, partial: adjustedPartial(row), unrankable: belowFloor(row, ctx.thresholds) })
    case 'cpa':
      return figure({ value: row.derived.cpa.value, decimals: 2, partial: row.derived.cpa.partial, unrankable: belowFloor(row, ctx.thresholds) })
    case 'aov':
      return figure({ value: row.derived.aov.value, decimals: 2, partial: row.derived.aov.partial, unrankable: belowFloor(row, ctx.thresholds) })
    case 'cpc':
      return figure({ value: row.derived.cpc.value, decimals: 2, partial: row.derived.cpc.partial })
    case 'cpm':
      return figure({ value: row.derived.cpm.value, decimals: 2, partial: row.derived.cpm.partial })
    case 'activeDays':
      return figure({ value: row.activeDays, decimals: 0, partial: incomplete('impressions') })
    case 'ads':
      return figure({ value: row.ads.length, decimals: 0 })
    case 'verdict': {
      if (row.verdict === null) return textCell('')
      return textCell(qualifiedLabel(verdictLabel(row.verdict.key, t), verdictQualifier(row, t)))
    }
  }
}

/**
 * The two text columns that follow «Идея и теги» (GRO-171): the message and the offer,
 * an empty cell where none is set.
 */
function tagCells(row: Row, ctx: ViewContext): ExportCell[] {
  return [
    labelOf(ctx.dimensions.messages, row.messageId, ctx.lang),
    labelOf(ctx.dimensions.offers, row.offerId, ctx.lang),
  ].map((label) => (label === null ? { kind: 'empty' } : textCell(label)))
}

/**
 * The columns the file carries: the visible ones, plus the accounts column whenever a row
 * ran in a demo or a lagging account. A hidden column must not strip a label that
 * qualifies every figure in its row, so it comes back in its usual place.
 */
export function exportedColumns(rows: Row[], visible: readonly ColumnKey[], ctx: ViewContext): ColumnKey[] {
  const flagged = rows.some((row) =>
    row.ads.some((ad) => {
      const account = ctx.accounts.find((candidate) => candidate.id === ad.accountId)
      return account?.isDemo === true || account?.lagging === true
    }),
  )
  const wanted = new Set<ColumnKey>(visible)
  if (flagged) wanted.add('accounts')
  return COLUMN_KEYS.filter((key) => wanted.has(key))
}

export function exportCells(rows: Row[], visible: readonly ColumnKey[], ctx: ViewContext): ExportTable {
  const defs = columnDefs(ctx.t)
  const columns = exportedColumns(rows, visible, ctx)
  const header = [
    ...columns.flatMap((key) => {
      const name = MONEY.has(key) && ctx.currency !== null ? `${defs[key].header}, ${ctx.currency}` : defs[key].header
      return key === 'hypothesis' ? [name, ctx.t.message, ctx.t.offer] : [name]
    }),
    // Always last and always present: a display name is an ad name, two creatives can
    // share one, and the file must not identify a creative by its name.
    ctx.t.csvCreativeId,
  ]
  const records = rows.map((row) => [
    ...columns.flatMap((key) => (key === 'hypothesis' ? [cell(row, key, ctx), ...tagCells(row, ctx)] : [cell(row, key, ctx)])),
    textCell(row.id),
  ])
  return { header, records }
}

function csvField(cell: ExportCell): string {
  switch (cell.kind) {
    case 'number':
      return cell.value.toFixed(cell.decimals)
    case 'text':
      return field(text(cell.text))
    case 'empty':
      return ''
  }
}

export function toCsv(rows: Row[], visible: readonly ColumnKey[], ctx: ViewContext): string {
  const { header, records } = exportCells(rows, visible, ctx)
  const lines = [header.map(field), ...records.map((record) => record.map(csvField))]
  return BOM + lines.map((line) => line.join(',')).join(EOL) + EOL
}

/**
 * No company, account or creative name: the file may travel further than the screen.
 *
 * A preset is the last N days as of the export, so the export date and the length name
 * it. A range can end anywhere in the stored history, and the same pair would misname it
 * as the days up to today, so a range is named by its own two days.
 */
export function exportFileName(
  ctx: Pick<ViewContext, 'periodDays' | 'isDemo'>,
  today: Date,
  period: Period | undefined,
  ext: 'csv' | 'xlsx',
): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const date = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
  const window = period?.kind === 'range' ? `${period.from}-to-${period.to}` : `${date}-${ctx.periodDays}d`
  return `grovus-creatives-${window}${ctx.isDemo ? '-demo' : ''}.${ext}`
}

export function csvFileName(ctx: Pick<ViewContext, 'periodDays' | 'isDemo'>, today: Date, period?: Period): string {
  return exportFileName(ctx, today, period, 'csv')
}
